import { Box, FormControlLabel, InputAdornment, Stack, Switch, TextField } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { DetailsPatch, DetailsValues } from '../state';

import { FIELD_HEIGHT_SX, FieldHelp, FormField } from './FormField';

/**
 * Resource planning, edited — in the When form since 3 Nov (decision 18):
 * show in the planner, and, before go-live (every Draft project, decision 2),
 * how likely it is to go ahead.
 */
export type ResourcePlanningFieldsProps = {
  draft: Pick<DetailsValues, 'includeInResourcePlanning' | 'likelihoodPct'>;
  onChange: (patch: DetailsPatch) => void;
};

/** Whole numbers 0–100 from whatever was typed; `null` while it is not one. */
const parsePct = (raw: string): number | null => {
  if (!/^\d{1,3}$/.test(raw.trim())) return null;
  const value = Number(raw.trim());
  return value <= 100 ? value : null;
};

export const ResourcePlanningFields = ({ draft, onChange }: ResourcePlanningFieldsProps) => {
  const { t } = useTranslation('pmDetails');
  // The box's own text, so it can be emptied while typing; the draft only
  // ever takes a whole number 0–100.
  const [pctText, setPctText] = useState(String(draft.likelihoodPct));
  return (
    <Stack sx={{ gap: 1.5 }}>
      <FormControlLabel
        sx={{ mx: 0, gap: 1, minHeight: 'var(--input-height-medium)' }}
        control={
          <Switch
            checked={draft.includeInResourcePlanning}
            onChange={(event) => onChange({ includeInResourcePlanning: event.target.checked })}
            slotProps={{ input: { 'data-automation-id': 'details-in-planner' } as object }}
          />
        }
        label={t('when.ShowInPlanner')}
      />
      <Box sx={{ maxWidth: 200 }}>
        <FormField
          label={t('when.LikelyLabel')}
          htmlFor="details-likelihood"
          below={<FieldHelp>{t('when.LikelyHelp')}</FieldHelp>}
        >
          <TextField
            id="details-likelihood"
            size="small"
            sx={FIELD_HEIGHT_SX}
            value={pctText}
            onChange={(event) => {
              setPctText(event.target.value);
              const pct = parsePct(event.target.value);
              if (pct !== null) onChange({ likelihoodPct: pct });
            }}
            // Leaving a half-typed value puts the last valid number back.
            onBlur={() => setPctText(String(draft.likelihoodPct))}
            slotProps={{
              htmlInput: { inputMode: 'numeric', 'data-automation-id': 'details-likelihood' },
              input: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
            }}
          />
        </FormField>
      </Box>
    </Stack>
  );
};
